import {
  IVnFptSigningParameters,
  IVnFptSigningSettings,
} from '@store/modules/user/types';

export type VnFptSigningSettingsKey = 'personal' | 'organisation';

type LegacyVnFptSigningParameters = IVnFptSigningParameters & {
  passcode?: string;
};

const removeStoredPasscode = (
  parameters: IVnFptSigningParameters
): IVnFptSigningParameters => {
  const safeParameters: LegacyVnFptSigningParameters = { ...parameters };

  delete safeParameters.passcode;

  return safeParameters;
};

export const getVnFptSigningSettingsKey = (
  isSignOnBehalfOfOrganization: boolean
): VnFptSigningSettingsKey =>
  isSignOnBehalfOfOrganization ? 'organisation' : 'personal';

export const getNextVnFptSigningSettings = (
  currentSettings: IVnFptSigningSettings | undefined,
  key: VnFptSigningSettingsKey,
  parameters: IVnFptSigningParameters
): IVnFptSigningSettings => ({
  ...sanitizeVnFptSigningSettings(currentSettings),
  [key]: removeStoredPasscode(parameters),
});

export const hasStoredVnFptPasscode = (
  settings: IVnFptSigningSettings | undefined
): boolean =>
  [settings?.personal, settings?.organisation].some((parameters) =>
    Boolean(
      parameters &&
      Object.prototype.hasOwnProperty.call(parameters, 'passcode')
    )
  );

export const sanitizeVnFptSigningSettings = (
  settings: IVnFptSigningSettings | undefined
): IVnFptSigningSettings | undefined => {
  if (!settings) return undefined;

  return {
    personal: settings.personal
      ? removeStoredPasscode(settings.personal)
      : undefined,
    organisation: settings.organisation
      ? removeStoredPasscode(settings.organisation)
      : undefined,
  };
};

export const canReuseVnFptSigningParameters = (
  parameters: IVnFptSigningParameters | undefined,
  isAssignedToBoxUser: boolean,
  requiresOrganizationSigning: boolean
): parameters is IVnFptSigningParameters => {
  if (
    !parameters?.providerUserIdentity ||
    (!isAssignedToBoxUser &&
      !(requiresOrganizationSigning && parameters.isSignerNameOmitted) &&
      !parameters.signerFullName)
  ) {
    return false;
  }

  if (!requiresOrganizationSigning) return true;

  return Boolean(
    parameters.organisationDetails?.companyName &&
    parameters.organisationDetails.taxCode &&
    parameters.organisationDetails.stampBase64
  );
};
